import { ErrorsStack } from "components/common/errorsDeclarations";

// ============================================
// COSTANTI
// ============================================

export const STATI_ORDINE_LAVORO = {
    'CONF': 'Confermato/Venduto',
    'ARR': 'Arrivato a terra',
    'DONE': 'Prodotto/Spedito',
    'AN': 'Annullato',
} as const;

export type StatoOrdineLavoro = keyof typeof STATI_ORDINE_LAVORO;

// ============================================
// INTERFACE ASSEGNAZIONE
// ============================================

export interface Assegnazione {
    id?: number;
    macchina?: number;  // Solo ID
    macchina_display?: string;  // Read-only dal backend
    ordine_di_lavoro?: number;  // Solo ID
    ordine_di_lavoro_display?: string;  // Read-only dal backend
    data_da?: string | Date | null;
    data_a?: string | Date | null;
    note?: string;
    valore_schedulato?: number;
    errorsStack?: ErrorsStack;
}

// ============================================
// INTERFACE ENTITÀ
// ============================================



export interface OrdineLavoro {
    id?: number;

    // Relazione previsione
    previsione?: number;  // Solo ID
    previsione_display?: string;  // Read-only dal backend

    // Campi dalla previsione (read-only, per comodità nel frontend)
    codice_item?: string;
    descrizione_item?: string;
    articolo_cliente?: string;
    stagione_display?: string;
    mandatario_display?: string;
    confezionista_display?: string;
    tecnica?: number;
    tecnica_display?: string;

    // Stato
    stato?: StatoOrdineLavoro | string;
    stato_display?: string;  // Read-only dal backend

    // Quantità
    qta_prevista?: number | string;
    qta_arrivata?: number | string;

    // Date
    data_arrivo_prevista?: string | Date | null;
    data_consegna_prevista?: string | Date | null;
    data_ricevimento?: string | Date | null;

    // Campi calcolati (read-only)
    giorni_ritardo?: number | null;
    quantita_difforme?: number | null;

    // Note
    note?: string;

    // Assegnazioni nested (read-only dal backend, CRUD separato)
    assegnazioni?: Assegnazione[];
    valore_confermato?: number;
    valore_schedulato?: number;
    valore_prodotto?: number;


    // Gestione errori frontend
    errorsStack?: ErrorsStack;
}

// ============================================
// INTERFACE LISTA PAGINATA
// ============================================

export interface OrdiniLavoroList {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: OrdineLavoro[];
    errorsStack: ErrorsStack;
}

// ============================================
// UTILITIES
// ============================================

/**
 * Crea un OrdineLavoro vuoto con defaults
 * @param previsioneId - ID della previsione (opzionale per lista generale)
 */
export const createOrdineLavoroVuoto = (previsioneId?: number): OrdineLavoro => ({
    previsione: previsioneId,
    stato: 'CONF',
    qta_prevista: undefined,
    data_arrivo_prevista: undefined,
    data_consegna_prevista: undefined,
    qta_arrivata: undefined,
    data_ricevimento: undefined,
    note: '',
    assegnazioni: [],
});

/**
 * Crea un'Assegnazione vuota con defaults
 * @param ordineLavoroId - ID dell'ordine di lavoro
 */
export const createAssegnazioneVuota = (ordineLavoroId?: number): Assegnazione => ({
    ordine_di_lavoro: ordineLavoroId,
    macchina: undefined,
    data_da: undefined,
    data_a: undefined,
    note: '',
});

/**
 * Verifica se un ordine di lavoro è in ritardo
 */
export const isOrdineLavoroInRitardo = (ordineLavoro: OrdineLavoro): boolean => {
    return (ordineLavoro.giorni_ritardo ?? 0) > 0 && ordineLavoro.stato !== 'AN';
};

/**
 * Restituisce il colore del chip in base allo stato
 */
export const getStatoOrdineLavoroColor = (
    stato?: string
): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (stato) {
        case 'CONF':
            return 'info';
        case 'ARR':
            return 'warning';
        case 'DONE':
            return 'success';
        case 'AN':
            return 'error';
        default:
            return 'default';
    }
};